import { Fingerprint, Flag, Loader2, LogOut, Wallet } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useBrowserWalletStore } from '@/features/browserWallet/store';
// deep imports: only what this dialog renders, not the flow's other dialogs.
import FeatureFlagsView from '@/features/flow/components/FeatureFlagsView';
import { useFlowStore } from '@/features/flow/store';
// the store module only, not the feature barrel: the barrel pulls the
// onboarding flow back into this module's own dependencies.
import { useOnboardingLauncher } from '@/features/onboarding/store';
import {
    clearLocalOAuthTradingAuthority,
    useTradingSessionStore,
} from '@/features/tradingSession';
import { broadcastTradingSession } from '@/features/tradingSession/broadcast';
import { performAccountLogout } from '@/features/tradingSession/logout';
import { usePasskeyManagementStore } from '@/features/tradingSession/passkeyManagementStore';
import {
    anchorLabel,
    useAnchorChain,
    useWallet,
    useWalletNetwork,
} from '@/features/wallet';
import { describeErrorText } from '@/lib/errors';

import { useOAuthSession } from '../hooks/useOAuthSession';
import { useOAuthBindingDialogStore } from '../oauthBindingDialogStore';
import { useSignInDialogStore } from '../signInDialogStore';
import {
    isOAuthAccountBindingRecoveryRequiredError,
    isOAuthAccountRemovalPendingError,
    removeOAuthAccountBindingWithWallet,
    verifyOAuthAccountBindingRemoved,
} from '../walletAccountBinding';

import { OAUTH_PROVIDER_NAMES, OAuthProviderLogo } from './OAuthProviderLogo';
import SocialLoginButtons from './SocialLoginButtons';

const SignInDialog: React.FC = () => {
    const isOpen = useSignInDialogStore((state) => state.isOpen);
    const view = useSignInDialogStore((state) => state.view);
    const close = useSignInDialogStore((state) => state.close);
    const openOAuthAccount = useSignInDialogStore(
        (state) => state.openOAuthAccount,
    );
    const openFeatureFlags = useSignInDialogStore(
        (state) => state.openFeatureFlags,
    );
    const backFromFeatureFlags = useSignInDialogStore(
        (state) => state.backFromFeatureFlags,
    );
    // V2 (the browser wallet flow): the owner-wallet binding and passkeys are
    // V1 trading-session artefacts, so its account window offers neither.
    const isBrowserWalletFlow = useFlowStore((state) => state.active === 'v2');
    const browserWalletAddress = useBrowserWalletStore(
        (state) => state.address,
    );
    const openPasskeyManagement = usePasskeyManagementStore(
        (state) => state.open,
    );
    const requestOAuthBinding = useOAuthBindingDialogStore(
        (state) => state.request,
    );
    const protectOAuthBinding = useOAuthBindingDialogStore(
        (state) => state.protect,
    );
    const releaseOAuthBinding = useOAuthBindingDialogStore(
        (state) => state.release,
    );
    const requestReauthentication = useTradingSessionStore(
        (state) => state.requestReauthentication,
    );
    const requestConnectedWalletWindow = useTradingSessionStore(
        (state) => state.requestConnectedWalletWindow,
    );
    const launchOnboarding = useOnboardingLauncher((state) => state.launch);
    const { address, isConnected, connect, disconnectWallet } = useWallet();
    const { ensureAnchor } = useWalletNetwork();
    const { anchorChainId } = useAnchorChain();
    const {
        isAuthenticated: isOAuthAuthenticated,
        provider,
        accountHash,
        bindingState,
        custodialWalletAddress,
        ownerAddress,
        displayName,
        username,
        rebind,
    } = useOAuthSession();
    const [error, setError] = useState<string | null>(null);
    const [managementAction, setManagementAction] = useState<
        'unlink' | 'switch' | null
    >(null);
    const [isWorking, setIsWorking] = useState(false);
    const [removedOwner, setRemovedOwner] = useState<string | null>(null);
    const [removalNeedsRecheck, setRemovalNeedsRecheck] = useState(false);
    const [registryRecoveryRequired, setRegistryRecoveryRequired] =
        useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setError(null);
        setManagementAction(null);
        setIsWorking(false);
        setRemovedOwner(null);
        setRemovalNeedsRecheck(false);
        setRegistryRecoveryRequired(false);
    }, [isOpen, view]);

    const chooseTradingWindow = (method: 'passkey' | 'eip712') => {
        close();
        if (method === 'eip712') {
            // one signature, default window, no picker: the wallet dialog
            // asks for it by itself
            requestConnectedWalletWindow();
            return;
        }
        requestReauthentication('manual', method);
    };

    // Creating a Snappy account is signed by an owner EOA, so a new user is
    // taken through onboarding wallet-first. Social sign-in alone (the usual
    // mobile entry) can never complete it.
    const createAccount = () => {
        close();
        launchOnboarding('welcome', {
            resumeReauthOnFinish: true,
            walletFirst: true,
        });
    };

    const finishRemoval = async (
        owner: string,
        continueWithSwitch: boolean,
    ) => {
        const rebound = await rebind();
        if (rebound.bindingState !== 'unbound') {
            throw new Error(
                'The identity was removed on-chain, but the session has not refreshed yet. Retry without signing another transaction.',
            );
        }

        broadcastTradingSession({ type: 'changed' });
        if (!continueWithSwitch) {
            releaseOAuthBinding(accountHash!);
            close();
            return;
        }
        try {
            await disconnectWallet();
        } catch {
            // The binding dialog still hard-blocks this owner if AppKit keeps it.
        }
        close();
        requestOAuthBinding({
            provider: provider!,
            accountHash: accountHash!,
            excludedOwner: owner,
        });
    };

    const removeBinding = async () => {
        if (!provider || !accountHash || !custodialWalletAddress) {
            setError('The linked OAuth account is not available.');
            return;
        }
        if (!managementAction) {
            setError('Choose whether to unlink or switch wallets.');
            return;
        }
        if (!removedOwner && (!address || !isConnected)) {
            await connect();
            return;
        }

        setError(null);
        setIsWorking(true);
        const owner = removedOwner ?? address!;
        const failClosed = () => {
            setRemovedOwner(owner);
            protectOAuthBinding(accountHash, owner);
            clearLocalOAuthTradingAuthority();
            broadcastTradingSession({ type: 'oauth-authority-cleared' });
        };
        try {
            if (registryRecoveryRequired) {
                try {
                    if (
                        !(await verifyOAuthAccountBindingRemoved(
                            custodialWalletAddress,
                            accountHash,
                            anchorChainId,
                        ))
                    ) {
                        setRemovedOwner(null);
                        setRegistryRecoveryRequired(false);
                        releaseOAuthBinding(accountHash);
                        broadcastTradingSession({ type: 'changed' });
                        throw new Error(
                            'The login hash is active again. You can retry the unlink transaction.',
                        );
                    }
                    setRegistryRecoveryRequired(false);
                } catch (caught) {
                    if (isOAuthAccountBindingRecoveryRequiredError(caught)) {
                        failClosed();
                    }
                    throw caught;
                }
            } else if (removalNeedsRecheck) {
                try {
                    if (
                        !(await verifyOAuthAccountBindingRemoved(
                            custodialWalletAddress,
                            accountHash,
                            anchorChainId,
                        ))
                    ) {
                        setRemovedOwner(null);
                        setRemovalNeedsRecheck(false);
                        releaseOAuthBinding(accountHash);
                        broadcastTradingSession({ type: 'changed' });
                        throw new Error(
                            'The previous unlink transaction did not complete. You can try the owner transaction again.',
                        );
                    }
                    setRemovalNeedsRecheck(false);
                } catch (caught) {
                    if (isOAuthAccountBindingRecoveryRequiredError(caught)) {
                        setRemovalNeedsRecheck(false);
                        setRegistryRecoveryRequired(true);
                        failClosed();
                    }
                    throw caught;
                }
            } else if (!removedOwner) {
                if (!(await ensureAnchor())) {
                    throw new Error(
                        `Switch the connected owner wallet to ${anchorLabel(anchorChainId)}.`,
                    );
                }
                try {
                    await removeOAuthAccountBindingWithWallet(
                        owner,
                        custodialWalletAddress,
                        accountHash,
                        anchorChainId,
                    );
                } catch (caught) {
                    if (isOAuthAccountRemovalPendingError(caught)) {
                        setRemovalNeedsRecheck(true);
                        failClosed();
                    } else if (
                        isOAuthAccountBindingRecoveryRequiredError(caught)
                    ) {
                        setRegistryRecoveryRequired(true);
                        failClosed();
                    }
                    throw caught;
                }
                failClosed();
            }
            await finishRemoval(owner, managementAction === 'switch');
        } catch (caught) {
            setError(describeErrorText(caught, { operation: 'Sign-in' }));
        } finally {
            setIsWorking(false);
        }
    };

    const signOut = async () => {
        setError(null);
        setIsWorking(true);
        try {
            await performAccountLogout();
            close();
        } catch (caught) {
            setError(describeErrorText(caught, { operation: 'Sign-out' }));
        } finally {
            setIsWorking(false);
        }
    };

    const isOAuthAccountView = view === 'oauth-account';
    const isAccountView = view === 'account';
    const isFeatureFlagsView = view === 'feature-flags';
    const isBrowserWalletAccountView =
        isOAuthAccountView && isBrowserWalletFlow;
    const providerName = provider ? OAUTH_PROVIDER_NAMES[provider] : 'OAuth';
    const identityLabel = username?.trim() || displayName?.trim() || null;
    const canManageOAuthBinding =
        isOAuthAuthenticated &&
        !!provider &&
        !!accountHash &&
        bindingState === 'bound' &&
        !!custodialWalletAddress;
    const canLinkOAuthBinding =
        isOAuthAuthenticated &&
        !!provider &&
        !!accountHash &&
        bindingState === 'unbound';
    let unlinkButtonLabel = 'Unlink and choose another wallet';
    if (registryRecoveryRequired) {
        unlinkButtonLabel = 'Recheck registry status';
    } else if (removalNeedsRecheck) {
        unlinkButtonLabel = 'Recheck unlink status';
    } else if (removedOwner) {
        unlinkButtonLabel = 'Retry session refresh';
    } else if (!isConnected || !address) {
        unlinkButtonLabel = 'Connect current owner wallet';
    } else if (managementAction === 'unlink') {
        unlinkButtonLabel = `Confirm unlink ${providerName}`;
    }

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(next) => {
                if (!next && !isWorking) close();
            }}
        >
            <DialogContent
                className="sm:max-w-md"
                // these views have no description by design
                {...(isFeatureFlagsView || isBrowserWalletAccountView
                    ? { 'aria-describedby': undefined }
                    : {})}
            >
                {isFeatureFlagsView ? (
                    <>
                        <DialogHeader>
                            <DialogTitle>Feature flags</DialogTitle>
                        </DialogHeader>
                        <FeatureFlagsView onBack={backFromFeatureFlags} />
                    </>
                ) : isBrowserWalletAccountView ? (
                    <>
                        <DialogHeader>
                            <DialogTitle>{providerName} account</DialogTitle>
                        </DialogHeader>

                        <div className="rounded-xl border border-border bg-secondary/30 p-4">
                            <div className="flex items-center gap-3">
                                {provider && (
                                    <OAuthProviderLogo
                                        provider={provider}
                                        className="size-6"
                                    />
                                )}
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-semibold text-foreground">
                                        {identityLabel ?? providerName}
                                    </p>
                                    {browserWalletAddress && (
                                        <p className="truncate font-mono text-xs text-muted-foreground">
                                            {browserWalletAddress}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <Button
                            type="button"
                            variant="outline"
                            onClick={openFeatureFlags}
                        >
                            <Flag className="size-4" />
                            Manage feature flags
                        </Button>

                        {isOAuthAuthenticated && (
                            <Button
                                type="button"
                                variant="ghost"
                                disabled={isWorking}
                                onClick={() => void signOut()}
                            >
                                <LogOut className="size-4" />
                                Sign out
                            </Button>
                        )}
                    </>
                ) : isAccountView ? (
                    <>
                        <DialogHeader>
                            <DialogTitle>Your account</DialogTitle>
                            <DialogDescription>
                                Manage how you sign in to this account.
                            </DialogDescription>
                        </DialogHeader>

                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                close();
                                openPasskeyManagement();
                            }}
                        >
                            <Fingerprint className="size-4" />
                            Manage passkeys
                        </Button>

                        <Button
                            type="button"
                            variant="outline"
                            onClick={openFeatureFlags}
                        >
                            <Flag className="size-4" />
                            Manage feature flags
                        </Button>

                        {isOAuthAuthenticated && provider ? (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={openOAuthAccount}
                            >
                                <OAuthProviderLogo
                                    provider={provider}
                                    className="size-4"
                                />
                                Manage {providerName} login
                            </Button>
                        ) : (
                            <section
                                aria-labelledby="account-add-social"
                                className="flex flex-col gap-3"
                            >
                                <h3
                                    id="account-add-social"
                                    className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                                >
                                    Add a social login
                                </h3>
                                <p className="text-sm text-muted-foreground">
                                    Optional. Adding one lets you sign in
                                    without your wallet. It is added to the
                                    account you already have, and your wallet
                                    stays its owner.
                                </p>
                                <SocialLoginButtons
                                    layout="providerGrid"
                                    onError={setError}
                                />
                                <p className="text-xs text-muted-foreground">
                                    You approve one Cronos transaction from your
                                    owner wallet to finish the link.
                                </p>
                            </section>
                        )}
                    </>
                ) : isOAuthAccountView ? (
                    <>
                        <DialogHeader>
                            <DialogTitle>{providerName} account</DialogTitle>
                            <DialogDescription>
                                Manage which owner wallet may use this login.
                            </DialogDescription>
                        </DialogHeader>

                        {canManageOAuthBinding ? (
                            <>
                                <div className="rounded-xl border border-border bg-secondary/30 p-4">
                                    <div className="flex items-center gap-3">
                                        <OAuthProviderLogo
                                            provider={provider}
                                            className="size-6"
                                        />
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-semibold text-foreground">
                                                {identityLabel ?? providerName}
                                            </p>
                                            {/* the LINKED OWNER EOA, never the custodial wallet — operators and users
                                                read this as "which wallet is this login attached to". */}
                                            <p className="truncate font-mono text-xs text-muted-foreground">
                                                {ownerAddress ?? '—'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {!managementAction ? (
                                    <>
                                        <p className="text-sm text-muted-foreground">
                                            Depositing is not required to unlink
                                            or switch, and the custodial wallet
                                            can stay empty. The connected owner
                                            wallet only needs enough CRO for one
                                            Cronos transaction. Existing funds
                                            stay where they are; only this{' '}
                                            {providerName} login is removed.
                                        </p>
                                        <div className="grid gap-2 sm:grid-cols-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={() =>
                                                    setManagementAction(
                                                        'unlink',
                                                    )
                                                }
                                            >
                                                Unlink {providerName}
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={() =>
                                                    setManagementAction(
                                                        'switch',
                                                    )
                                                }
                                            >
                                                Switch linked wallet
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm text-muted-foreground">
                                            The current owner must approve one
                                            Cronos transaction.{' '}
                                            {managementAction === 'switch'
                                                ? 'Trading with this login pauses until you link the intended wallet.'
                                                : `This removes ${providerName} from this wallet without deleting the custodial wallet or moving its funds.`}
                                        </div>
                                        <Button
                                            type="button"
                                            variant="destructive"
                                            disabled={isWorking}
                                            onClick={() => void removeBinding()}
                                        >
                                            {isWorking && (
                                                <Loader2 className="size-4 animate-spin" />
                                            )}
                                            {unlinkButtonLabel}
                                        </Button>
                                    </>
                                )}
                            </>
                        ) : canLinkOAuthBinding ? (
                            <>
                                <p className="text-sm text-muted-foreground">
                                    This {providerName} login is not linked to
                                    an owner wallet.
                                </p>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        close();
                                        requestOAuthBinding({
                                            provider,
                                            accountHash,
                                        });
                                    }}
                                >
                                    Link an owner wallet
                                </Button>
                            </>
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                This OAuth session does not currently have a
                                linked wallet to manage.
                            </p>
                        )}

                        {!managementAction && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    close();
                                    openPasskeyManagement();
                                }}
                            >
                                <Fingerprint className="size-4" />
                                Manage passkeys
                            </Button>
                        )}

                        {!managementAction && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={openFeatureFlags}
                            >
                                <Flag className="size-4" />
                                Manage feature flags
                            </Button>
                        )}

                        {/* always reachable from here, linked or not: after an
                            unlink this view is the only account surface left,
                            and the header hides its own Sign out once the app
                            token is gone. */}
                        {!managementAction && isOAuthAuthenticated && (
                            <Button
                                type="button"
                                variant="ghost"
                                disabled={isWorking}
                                onClick={() => void signOut()}
                            >
                                <LogOut className="size-4" />
                                Sign out
                            </Button>
                        )}
                    </>
                ) : isBrowserWalletFlow ? (
                    // V2: the browser wallet's backups belong to a social
                    // account, and a wallet or passkey trading window (and the
                    // wallet-first account creation) are V1's ways in.
                    <>
                        <DialogHeader>
                            <DialogTitle>Sign in to Snappy</DialogTitle>
                            <DialogDescription>
                                Sign in with the account that holds your Snappy
                                Wallet backups.
                            </DialogDescription>
                        </DialogHeader>

                        <SocialLoginButtons
                            layout="providerGrid"
                            onError={setError}
                        />
                    </>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle>Sign in to Snappy</DialogTitle>
                            <DialogDescription>
                                Sign in the way you signed up, or create a new
                                account.
                            </DialogDescription>
                        </DialogHeader>

                        <section
                            aria-labelledby="sign-in-existing"
                            className="flex flex-col gap-3"
                        >
                            <h3
                                id="sign-in-existing"
                                className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                            >
                                Already have an account
                            </h3>

                            {/* the wallet is the main way in: every account is owned by one */}
                            <Button
                                type="button"
                                size="lg"
                                className="w-full font-semibold"
                                onClick={() => chooseTradingWindow('eip712')}
                            >
                                <Wallet size={16} />
                                Connected Wallet
                            </Button>

                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span className="h-px flex-1 bg-border" />
                                <span>or</span>
                                <span className="h-px flex-1 bg-border" />
                            </div>

                            <SocialLoginButtons
                                layout="providerGrid"
                                onError={setError}
                                trailing={
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                            chooseTradingWindow('passkey')
                                        }
                                        aria-label="Passkey (face, fingerprint or PIN)"
                                        className="h-auto min-w-0 flex-col justify-center gap-2 px-2 py-3 hover:border-primary/50 hover:bg-primary/5 hover:text-foreground dark:hover:bg-primary/5"
                                    >
                                        <Fingerprint size={20} />
                                        <span className="truncate text-xs font-medium">
                                            Passkey
                                        </span>
                                    </Button>
                                }
                            />
                        </section>

                        <span className="h-px bg-border" />

                        <section
                            aria-labelledby="sign-in-new"
                            className="flex flex-col gap-3"
                        >
                            <h3
                                id="sign-in-new"
                                className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                            >
                                New here
                            </h3>

                            <Button
                                type="button"
                                size="lg"
                                className="w-full font-semibold"
                                onClick={createAccount}
                            >
                                <Wallet size={16} />
                                Create account
                            </Button>

                            <p className="text-xs text-muted-foreground">
                                A new Snappy account is created from a crypto
                                wallet on Cronos, so you connect one in the next
                                step.
                            </p>
                        </section>
                    </>
                )}

                {error && (
                    <p role="alert" className="text-sm text-destructive">
                        {error}
                    </p>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default SignInDialog;
